/**
 * @fileoverview Ad Rendering Module
 *
 * This module handles the creation and configuration of ad elements for display
 * in Chrome extensions. It builds DOM elements programmatically from structured
 * ad data - never using innerHTML or raw HTML strings for security.
 *
 * Key Features:
 * - Secure DOM-based ad rendering (no innerHTML)
 * - Automatic impression and click tracking
 * - Accessible image ads with alt text
 * - Performance optimizations
 *
 * The module prioritizes security and performance while providing a simple,
 * developer-friendly API for ad display.
 *
 * @author PlayaYield SDK Team
 * @version 2.0.0
 */

import type { AdCreative, AdResult } from './types';
import { MessageType } from './types';
import { trackImpression, trackClick } from './tracker';
import { sendToBackground } from './messaging';
import { logger } from './logger';

/**
 * Tracks IntersectionObserver instances per ad element.
 *
 * Used to prevent double-counting and to ensure that when a refresh swaps the creative
 * within the same DOM element, any previous observer is disconnected before a new one
 * is attached.
 */
const impressionObserverByElement = new WeakMap<
  HTMLElement,
  IntersectionObserver
>();

/**
 * Tracks the last impressionId observed for a given element.
 *
 * If the same creative/impressionId is re-rendered into the same element, we skip
 * re-attaching observers and re-firing tracking.
 */
const lastImpressionIdByElement = new WeakMap<HTMLElement, string>();

const LAYOUT_TOKENS = {
  card: {
    minImageHeight: 100,
    contentPadding: '10px',
    contentGap: '5px',
  },
  banner: {
    imageWidth: 100,
    contentPadding: '4px 10px',
    contentGap: '3px',
  },
  bar: {
    imageWidth: 50,
    contentPadding: '0 8px',
    contentGap: '2px',
  },
} as const;

function applyTextOverflowSafety(el: HTMLElement): void {
  el.style.minWidth = '0';
  el.style.overflowWrap = 'anywhere';
  el.style.wordBreak = 'break-word';
}

/**
 * Creates an Ad Choices icon element positioned at the top-right corner.
 * Clicking the icon opens the Ad Choices info page in a new tab.
 *
 * @param adChoicesUrl - URL to the Ad Choices icon image
 * @param provider - The ad provider name for the info page
 * @returns Configured HTMLImageElement with click handler
 */
function createAdChoicesIcon(
  adChoicesUrl: string,
  provider?: string
): HTMLImageElement {
  const icon = document.createElement('img');
  icon.src = adChoicesUrl;
  icon.alt = 'Ad Choices';
  icon.style.position = 'absolute';
  icon.style.top = '0';
  icon.style.right = '0';
  icon.style.width = '18px';
  icon.style.height = '18px';
  icon.style.zIndex = '10';
  icon.style.cursor = 'pointer';

  // Prevent click from bubbling to ad click handler
  icon.onclick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    const baseUrl = 'https://playayield.com/ad-choices';
    const providerParam = provider
      ? `?provider=${encodeURIComponent(provider)}`
      : '';
    window.open(`${baseUrl}${providerParam}`, '_blank', 'noopener,noreferrer');
  };

  return icon;
}

/**
 * Renders a display ad (image-only, no text elements).
 * Display ads are pre-designed creatives where all content is baked into the image.
 * The image fills the entire container with object-fit: cover.
 *
 * @param container - The container element to populate
 * @param creative - The ad creative data
 */
function renderDisplayAd(
  container: HTMLDivElement,
  creative: AdCreative
): void {
  const {
    width,
    height,
    imageUrl,
    imageAlt,
    backgroundColor,
    adChoicesUrl,
    provider,
  } = creative;

  container.innerHTML = '';

  // Base container styles
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;
  container.style.overflow = 'hidden';
  container.style.position = 'relative';
  container.style.boxSizing = 'border-box';
  container.style.backgroundColor = backgroundColor || '#f1f5f9';
  container.style.border = '1px solid #e2e8f0';

  // Full-bleed image
  const img = document.createElement('img');
  img.src = imageUrl;
  img.alt = imageAlt || 'Advertisement';
  img.style.width = '100%';
  img.style.height = '100%';
  img.style.objectFit = 'cover';
  img.style.display = 'block';
  container.appendChild(img);

  // Ad Choices icon
  if (adChoicesUrl) {
    container.appendChild(createAdChoicesIcon(adChoicesUrl, provider));
  }
}

/**
 * Populates the ad container with the correct layout based on ad format and size.
 *
 * For display ads (format: 'display'):
 * - Full-bleed image with no text elements
 *
 * For native ads (format: 'native' or undefined):
 * - 300x250: Vertical layout (image top, text bottom)
 * - 320x100: Horizontal layout (image left, text right)
 * - 320x50: Compact horizontal layout (image left, text right)
 */
function populateAdContainer(
  container: HTMLDivElement,
  creative: AdCreative
): void {
  const { format = 'native' } = creative;

  // Display ads: full-bleed image, no text
  if (format === 'display') {
    renderDisplayAd(container, creative);
    return;
  }

  // Native ads: size-based layouts with text content
  const {
    width,
    height,
    imageUrl,
    imageAlt,
    title,
    description,
    advertiser,
    backgroundColor,
    adChoicesUrl,
    provider,
  } = creative;
  container.innerHTML = '';

  // Base container styles
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;
  container.style.overflow = 'hidden';
  container.style.position = 'relative';
  container.style.boxSizing = 'border-box';
  container.style.display = 'flex';
  container.style.backgroundColor = backgroundColor || '#ffffff';
  container.style.border = '1px solid #e2e8f0';
  container.style.fontFamily = 'system-ui, -apple-system, sans-serif';
  container.style.color = '#0f172a';
  container.style.textAlign = 'left';
  container.style.lineHeight = 'normal';
  container.style.letterSpacing = 'normal';
  container.style.wordBreak = 'break-word';
  container.style.overflowWrap = 'anywhere';

  if (width === 300 && height === 250) {
    container.style.flexDirection = 'column';

    const useTightTextLayout = !!advertiser && (title?.length ?? 0) > 64;

    const imgWrapper = document.createElement('div');
    imgWrapper.style.width = '100%';
    imgWrapper.style.flexGrow = '1';
    imgWrapper.style.flexShrink = '1';
    imgWrapper.style.minHeight = `${LAYOUT_TOKENS.card.minImageHeight}px`;
    imgWrapper.style.backgroundColor = '#f1f5f9';

    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = imageAlt || 'Advertisement';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    img.style.display = 'block';

    imgWrapper.appendChild(img);
    container.appendChild(imgWrapper);

    const contentWrapper = document.createElement('div');
    contentWrapper.style.padding = LAYOUT_TOKENS.card.contentPadding;
    contentWrapper.style.display = 'flex';
    contentWrapper.style.flexDirection = 'column';
    contentWrapper.style.gap = LAYOUT_TOKENS.card.contentGap;
    contentWrapper.style.flexShrink = '0';
    contentWrapper.style.overflow = 'hidden';
    applyTextOverflowSafety(contentWrapper);

    if (advertiser) {
      const advertiserEl = document.createElement('div');
      advertiserEl.style.fontSize = '10px';
      advertiserEl.style.fontWeight = '600';
      advertiserEl.style.lineHeight = '1.2';
      advertiserEl.style.color = '#64748b';
      advertiserEl.style.textTransform = 'uppercase';
      advertiserEl.style.whiteSpace = 'nowrap';
      advertiserEl.style.textOverflow = 'ellipsis';
      advertiserEl.style.overflow = 'hidden';
      applyTextOverflowSafety(advertiserEl);
      advertiserEl.textContent = 'SPONSORED';
      contentWrapper.appendChild(advertiserEl);
    }

    if (title) {
      const titleEl = document.createElement('div');
      titleEl.style.fontSize = '15px';
      titleEl.style.fontWeight = '700';
      titleEl.style.lineHeight = '1.3';
      titleEl.style.display = '-webkit-box';
      titleEl.style.webkitLineClamp = '2';
      titleEl.style.webkitBoxOrient = 'vertical';
      titleEl.style.overflow = 'hidden';
      applyTextOverflowSafety(titleEl);
      titleEl.textContent = title;
      contentWrapper.appendChild(titleEl);
    }

    if (description) {
      const descEl = document.createElement('div');
      descEl.style.fontSize = '12px';
      descEl.style.lineHeight = '1.25';
      descEl.style.color = '#475569';
      descEl.style.display = '-webkit-box';
      descEl.style.webkitLineClamp = useTightTextLayout ? '2' : '3';
      descEl.style.webkitBoxOrient = 'vertical';
      descEl.style.overflow = 'hidden';
      applyTextOverflowSafety(descEl);
      descEl.textContent = description;
      contentWrapper.appendChild(descEl);
    }

    container.appendChild(contentWrapper);

    // Add Ad Choices icon if provided
    if (adChoicesUrl) {
      container.appendChild(createAdChoicesIcon(adChoicesUrl, provider));
    }
  } else if (width === 320 && height === 100) {
    container.style.flexDirection = 'row';

    const useTightTitleClamp = !!advertiser && (title?.length ?? 0) > 72;

    const imgWrapper = document.createElement('div');
    imgWrapper.style.width = `${LAYOUT_TOKENS.banner.imageWidth}px`;
    imgWrapper.style.height = '100%';
    imgWrapper.style.flexShrink = '0';
    imgWrapper.style.backgroundColor = '#f1f5f9';

    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = imageAlt || 'Advertisement';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    img.style.display = 'block';

    imgWrapper.appendChild(img);
    container.appendChild(imgWrapper);

    const contentWrapper = document.createElement('div');
    contentWrapper.style.padding = LAYOUT_TOKENS.banner.contentPadding;
    contentWrapper.style.display = 'flex';
    contentWrapper.style.flexDirection = 'column';
    contentWrapper.style.justifyContent = 'center';
    contentWrapper.style.gap = LAYOUT_TOKENS.banner.contentGap;
    contentWrapper.style.flexGrow = '1';
    contentWrapper.style.overflow = 'hidden';
    contentWrapper.style.minWidth = '0';
    contentWrapper.style.minHeight = '0';
    applyTextOverflowSafety(contentWrapper);

    if (advertiser) {
      const advertiserEl = document.createElement('div');
      advertiserEl.style.fontSize = '10px';
      advertiserEl.style.fontWeight = '600';
      advertiserEl.style.lineHeight = '1.15';
      advertiserEl.style.color = '#64748b';
      advertiserEl.style.textTransform = 'uppercase';
      advertiserEl.style.whiteSpace = 'nowrap';
      advertiserEl.style.textOverflow = 'ellipsis';
      advertiserEl.style.overflow = 'hidden';
      applyTextOverflowSafety(advertiserEl);
      advertiserEl.textContent = 'SPONSORED';
      contentWrapper.appendChild(advertiserEl);
    }

    if (title) {
      const titleEl = document.createElement('div');
      titleEl.style.fontSize = '12px';
      titleEl.style.fontWeight = '700';
      titleEl.style.lineHeight = '1.2';
      titleEl.style.display = '-webkit-box';
      titleEl.style.webkitLineClamp = useTightTitleClamp ? '2' : '3';
      titleEl.style.webkitBoxOrient = 'vertical';
      titleEl.style.overflow = 'hidden';
      applyTextOverflowSafety(titleEl);
      titleEl.textContent = title;
      contentWrapper.appendChild(titleEl);
    }

    if (description) {
      const descEl = document.createElement('div');
      descEl.style.fontSize = '11px';
      descEl.style.lineHeight = '1.2';
      descEl.style.color = '#475569';
      descEl.style.display = '-webkit-box';
      descEl.style.webkitLineClamp = '1';
      descEl.style.webkitBoxOrient = 'vertical';
      descEl.style.overflow = 'hidden';
      applyTextOverflowSafety(descEl);
      descEl.textContent = description;
      contentWrapper.appendChild(descEl);
    }

    container.appendChild(contentWrapper);

    // Add Ad Choices icon if provided
    if (adChoicesUrl) {
      container.appendChild(createAdChoicesIcon(adChoicesUrl, provider));
    }
  } else if (width === 320 && height === 50) {
    container.style.flexDirection = 'row';

    const imgWrapper = document.createElement('div');
    imgWrapper.style.width = `${LAYOUT_TOKENS.bar.imageWidth}px`;
    imgWrapper.style.height = '100%';
    imgWrapper.style.flexShrink = '0';
    imgWrapper.style.backgroundColor = '#f1f5f9';

    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = imageAlt || 'Advertisement';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    img.style.display = 'block';

    imgWrapper.appendChild(img);
    container.appendChild(imgWrapper);

    const contentWrapper = document.createElement('div');
    contentWrapper.style.padding = LAYOUT_TOKENS.bar.contentPadding;
    contentWrapper.style.display = 'flex';
    contentWrapper.style.flexDirection = 'column';
    contentWrapper.style.justifyContent = 'center';
    contentWrapper.style.gap = LAYOUT_TOKENS.bar.contentGap;
    contentWrapper.style.flexGrow = '1';
    contentWrapper.style.overflow = 'hidden';
    contentWrapper.style.minWidth = '0';
    contentWrapper.style.minHeight = '0';
    applyTextOverflowSafety(contentWrapper);

    if (title) {
      const titleEl = document.createElement('div');
      titleEl.style.fontSize = '13px';
      titleEl.style.fontWeight = '700';
      titleEl.style.lineHeight = '1.2';
      titleEl.style.whiteSpace = 'nowrap';
      titleEl.style.textOverflow = 'ellipsis';
      titleEl.style.overflow = 'hidden';
      applyTextOverflowSafety(titleEl);
      titleEl.textContent = title;
      contentWrapper.appendChild(titleEl);
    }

    if (advertiser) {
      const advertiserEl = document.createElement('div');
      advertiserEl.style.fontSize = '10px';
      advertiserEl.style.fontWeight = '600';
      advertiserEl.style.lineHeight = '1.1';
      advertiserEl.style.color = '#64748b';
      advertiserEl.style.textTransform = 'uppercase';
      advertiserEl.style.whiteSpace = 'nowrap';
      advertiserEl.style.textOverflow = 'ellipsis';
      advertiserEl.style.overflow = 'hidden';
      applyTextOverflowSafety(advertiserEl);
      advertiserEl.textContent = 'SPONSORED';
      contentWrapper.appendChild(advertiserEl);
    }

    container.appendChild(contentWrapper);

    // Add Ad Choices icon if provided
    if (adChoicesUrl) {
      container.appendChild(createAdChoicesIcon(adChoicesUrl, provider));
    }
  } else {
    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = imageAlt || 'Advertisement';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    img.style.display = 'block';
    container.appendChild(img);

    // Add Ad Choices icon if provided
    if (adChoicesUrl) {
      container.appendChild(createAdChoicesIcon(adChoicesUrl, provider));
    }
  }
}

/**
 * Tracks the available event (ad rendered to DOM) immediately.
 * This is Taboola-specific and fires before the ad becomes visible.
 *
 * @param creative The ad creative that was rendered.
 */
async function trackAdAvailable(creative: AdCreative): Promise<void> {
  // Only track available event if provider supports it (currently only Taboola)
  if (!creative.availableHash || !creative.provider) {
    return;
  }

  try {
    await sendToBackground({
      type: MessageType.TRACK_AVAILABLE,
      payload: {
        impressionId: creative.impressionId,
        provider: creative.provider,
        availableHash: creative.availableHash,
      },
    });
  } catch (error) {
    logger.error('Failed to track available event:', error);
  }
}

/**
 * Sets up viewport-based impression tracking for an ad element.
 *
 * Fires a single impression event when the ad element becomes visible in the viewport
 * (>= 50% visible). Uses IntersectionObserver when available.
 *
 * Safeguards:
 * - Disconnects any prior observer registered for this element (prevents late-firing
 *   observers from earlier creatives after refresh).
 * - Skips tracking if the same impressionId has already been registered for this element.
 *
 * @param element The rendered ad container element.
 * @param creative The ad creative that should be tracked when visible.
 */
function setupViewportImpressionTracking(
  element: HTMLElement,
  creative: AdCreative
): void {
  const { impressionId } = creative;

  const lastImpressionId = lastImpressionIdByElement.get(element);
  if (lastImpressionId === impressionId) {
    return;
  }

  lastImpressionIdByElement.set(element, impressionId);

  const priorObserver = impressionObserverByElement.get(element);
  if (priorObserver) {
    priorObserver.disconnect();
    impressionObserverByElement.delete(element);
  }

  const track = () => {
    trackImpression(impressionId, {
      type: 'image',
      provider: creative.provider,
      viewHash: creative.viewHash,
      impressionHash: creative.impressionHash,
    });
  };

  if (typeof IntersectionObserver === 'undefined') {
    // Fallback: track shortly after render
    setTimeout(track, 100);
    return;
  }

  let tracked = false;
  const observer = new IntersectionObserver(
    (entries) => {
      if (tracked) return;
      const entry = entries[0];
      if (!entry) return;
      if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
        tracked = true;
        observer.disconnect();
        impressionObserverByElement.delete(element);
        track();
      }
    },
    { threshold: [0.5] }
  );

  impressionObserverByElement.set(element, observer);
  observer.observe(element);
}

/**
 * Creates a ready-to-append HTML div element from structured ad creative data.
 *
 * This function handles the complete ad rendering process:
 * - Creates DOM elements programmatically (no innerHTML)
 * - Builds an image-based ad with optional title/description
 * - Sets up automatic impression tracking
 * - Configures click tracking with the click URL
 * - Returns a fully configured HTMLDivElement ready for DOM insertion
 *
 * @param creative - Structured ad creative data from the backend API
 * @returns AdResult object containing the rendered div element, impression ID, and click URL
 *
 * @throws {Error} When imageUrl is not provided in the creative
 *
 * @example
 * ```typescript
 * const { element, impressionId } = createAdElement(creative);
 * document.getElementById('ad-slot')!.appendChild(element);
 * ```
 *
 * @internal This function is used internally by the SDK and not exposed to developers
 */
export function createAdElement(creative: AdCreative): AdResult {
  const { impressionId, clickUrl, width, height, imageUrl } = creative;

  if (!imageUrl) {
    throw new Error('No imageUrl provided in ad creative');
  }

  const element = buildAdElement(creative);

  // Set up click tracking
  setupClickTracking(element, impressionId, clickUrl, creative.provider);

  return {
    element,
    impressionId,
    clickUrl,
  };
}

/**
 * Builds an ad container element using DOM APIs (no innerHTML).
 *
 * This function creates a div container with:
 * - Image element with the ad creative
 * - Optional title and description text
 * - Fixed dimensions to prevent layout shifts
 * - Hidden overflow to contain content within bounds
 * - Immediate impression tracking
 * - Data attributes for debugging and analytics
 *
 * @param creative - Structured ad creative data
 * @returns Configured HTMLDivElement with ad content
 *
 * @example
 * ```typescript
 * const container = buildAdElement(creative);
 * document.body.appendChild(container);
 * ```
 *
 * @internal Used internally by createAdElement
 */
function buildAdElement(creative: AdCreative): HTMLDivElement {
  const { impressionId } = creative;

  // Create container
  const container = document.createElement('div');
  container.setAttribute('data-playayield-id', impressionId);

  populateAdContainer(container, creative);

  // Track available event immediately (Taboola only)
  trackAdAvailable(creative);

  // Track impression when ad is visible in viewport
  setupViewportImpressionTracking(container, creative);

  return container;
}

/**
 * Configures click tracking and navigation behavior for ad elements.
 *
 * This function sets up comprehensive click handling for ad elements:
 * - Visual cursor indicator (pointer) to show element is clickable
 * - Click event interception to prevent default browser behavior
 * - Automatic click tracking with impression ID and click URL
 * - Secure navigation to click URL in new tab with security flags
 * - Prevents event bubbling to avoid conflicts with parent elements
 *
 * The function ensures that all ad clicks are properly tracked before
 * redirecting users to the advertiser's landing page.
 *
 * @param element - The HTML element to attach click tracking to
 * @param impressionId - Unique identifier for the ad impression being tracked
 * @param clickUrl - The destination URL to navigate to when clicked
 * @returns void - Modifies the element in place
 *
 * @example
 * ```typescript
 * const adElement = document.createElement('div');
 * setupClickTracking(adElement, 'imp_123', 'https://advertiser.example.com/landing');
 * document.body.appendChild(adElement);
 * ```
 *
 * @internal Used internally by createAdElement when clickUrl is provided
 */
function setupClickTracking(
  element: HTMLElement,
  impressionId: string,
  clickUrl: string,
  provider?: string
): void {
  element.style.cursor = 'pointer';

  element.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Track click
    trackClick(impressionId, {
      clickUrl,
      provider,
      referrer: document.referrer,
    });

    // Open click URL in new tab
    window.open(clickUrl, '_blank', 'noopener,noreferrer');
  };
}

/**
 * Updates an existing ad element with new creative data.
 * Used by refreshable ads to swap content without destroying the DOM element.
 *
 * This function:
 * - Clears the existing ad content
 * - Rebuilds the ad with new creative data
 * - Maintains the same container element (preserves DOM position)
 * - Re-attaches click tracking for the new creative
 * - Updates data attributes
 * - Tracks new impression
 *
 * @param element - The existing ad container element to update
 * @param creative - New ad creative data to display
 *
 * @example
 * ```typescript
 * const { element } = createAdElement(oldCreative);
 * document.body.appendChild(element);
 *
 * // Later, refresh with new creative:
 * updateAdElement(element, newCreative);
 * ```
 *
 * @internal Used internally by refreshable ads
 */
export function updateAdElement(
  element: HTMLDivElement,
  creative: AdCreative
): void {
  const { impressionId, clickUrl, width, height, imageUrl } = creative;

  if (!imageUrl) {
    throw new Error('No imageUrl provided in ad creative');
  }

  // Clear existing content but keep the element reference
  element.setAttribute('data-playayield-id', impressionId);

  populateAdContainer(element, creative);

  // Re-attach click tracking for the refreshed creative
  setupClickTracking(element, impressionId, clickUrl, creative.provider);

  // Track available event immediately (Taboola only)
  trackAdAvailable(creative);

  // Track new impression when refreshed ad is visible
  setupViewportImpressionTracking(element, creative);
}
